import { useQueryParameters } from '@/hooks/useQueryParameters';
import Autosave from '@oncolens/oncolens-ui/lib/components/Common/Autosave';
import ButtonWithLoading from '@oncolens/oncolens-ui/lib/components/FormControls/ButtonWithLoading';
import {
  CollaborationTemplateDto,
  RequestDto,
  RequestStatuses
} from '@oncolens/oncolens-ui/lib/dto/event/request';
import { FormStoreProps } from '@oncolens/oncolens-ui/lib/libraries/FormStore';
import Modal from '@oncolens/oncolens-ui/lib/components/Common/Modal';
import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { createPatient } from '../../api/createPatient';
import { createRequest } from '../../api/createRequest';
import { submitRequest } from '../../api/submitRequest';
import { updatePatient } from '../../api/updatePatient';
import { updateRequest } from '../../api/updateRequest';
import useRequestFormStore, { RequestFormStoreValues } from '../../RequestFormStore';

interface NewRequestSubmissionProps {
	template: CollaborationTemplateDto | undefined;
	showAutosave?: boolean;
	testSelectorPrefix?: string;
}

const NewRequestSubmission = ({
  template,
  showAutosave = true,
  testSelectorPrefix = 'new-request-submission'
}: NewRequestSubmissionProps) => {
  const { updateStore, isValid, getValues, dirtyFields, isDirty, markClean, schema } =
		useRequestFormStore();
  // In order to bypass consent during ANY autosave, we need to ignore what the store tells us
  // is valid.
  const isAutosaveValid =
		showAutosave && schema.isValidSync({ ...getValues(), hasGivenConsent: true });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const formValue = getValues();
  const requestId = useQueryParameters().get('requestId');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (showAutosave && isValid && dirtyFields.length > 0 && !requestId && Boolean(template)) {
      createNewRequest(getValues, onCreateSuccess);
    }
  }, [formValue, isValid, requestId]);

  const onSave = React.useCallback(
    (isSubmit?: boolean) => {
      const promises = [];
      const values = getValues();

      if (isDirty('patient')) {
        const organizationId = values.organizationId;

        promises.push(updatePatient(values.patient.id, { ...values.patient, organizationId }));
      }

      if (isDirty('submitTo') || isSubmit) {
        promises.push(
					updateRequest(values.id || '', {
            workflowId: values.workflowId ?? '',
					  isHighPriority: values.submitTo.highPriority,
					  topicForDiscussion: values.submitTo.reasonsForRequest,
					  preferredPhysicianId: values.submitTo.preferredPhysicianId,
					  isPending: isSubmit ? false : values.isPending // TODO remove from request
					}) as Promise<any>
        );
        isSubmit && promises.push(submitRequest(values.id || ''));
      }

      return Promise.all(promises).then(() => {
        markClean();
      });
    },
    [getValues, isDirty, markClean]
  );

  const onClickSave = (isConfirmAction: boolean) => {
    if (!isConfirmAction) {
      const values = getValues();
      if (
        (!values.status || values.status === RequestStatuses.PendingRequest) &&
				!isConfirmAction
      ) {
        setShowModal(true);
        return;
      }
    }

    setIsSubmitting(true);
    setShowModal(false);
    onSave(true)
      .then(() => {
        navigate(`/requests/splash?requestId=${formValue.id}`);
      })
      .catch(() => {
        setIsSubmitting(false);
      });
  };

  const onCreateSuccess = (request: RequestDto) => {
    markClean();
    updateStore((store: FormStoreProps<RequestFormStoreValues>) => {
      store.id = request.id;
      store.patient.id = request.patient.id;
      store.submissionId = request.displayInternalId;
    });
    navigate(`${location.pathname}?requestId=${request.id}`);
  };

  return (
    <div className="w-full sm:w-auto">
      <ButtonWithLoading
        className="justify-center w-full px-3 py-1.5 order-2 sm:order-none"
        loading={isSubmitting}
        disabled={!isValid}
        buttonColor="blue"
        onClick={() => onClickSave(false)}
        testSelector={`${testSelectorPrefix}-submit`}
      >
				Submit
      </ButtonWithLoading>
      {showAutosave ? (
        <div
          className={clsx(
            'transition-opacity delay-200 order-1 ml-auto sm:ml-0 sm:order-none',
            !requestId && 'opacity-0',
            requestId && 'opacity-100'
          )}
        >
          <Autosave
            value={formValue}
            enabled={Boolean(requestId) && isAutosaveValid}
            dirty={dirtyFields.length > 0}
            onSave={() => onSave(false)}
          />
        </div>
      ) : null}
      <Modal show={showModal} size="small">
        <Modal.Content>
          <p className="mb-2">
						By clicking “Submit”, you acknowledge sending clinical information and imaging to
						another physician for purposes of educational guidance only and does not constitute a
						formal request for consultation or second opinion. You further acknowledge any guidance
						you receive in response will not constitute a diagnosis, medical advice, treatment, or
						medical care, and will not establish a physician-patient relationship.
          </p>
          <p>Do you want to continue with your Submit Request?</p>
        </Modal.Content>
        <Modal.Buttons
          buttons={[
            {
              label: 'Cancel',
              style: 'secondary',
              color: 'gray',
              className: 'w-auto inline-block ml-auto mr-2 py-1.5 px-6',
              onClick: () => setShowModal(false),
              testSelector: `${testSelectorPrefix}-button-cancel`
            },
            {
              label: 'Submit',
              style: 'primary',
              color: 'blue',
              className: 'w-auto inline-block py-1.5 px-6',
              onClick: () => onClickSave(true),
              closeAfterClick: false,
              useLoading: true,
              loading: isSubmitting,
              disabled: !isValid,
              testSelector: `${testSelectorPrefix}-button-submit`
            }
          ]}
        />
      </Modal>
    </div>
  );
};

function createNewRequest(
  getValues: () => RequestFormStoreValues,
  onSuccess: (result: RequestDto) => void
) {
  const formData = getValues();
  const patientData = {
    ...formData.patient,
    organizationId: formData.organizationId
  };

  createPatient(patientData)
    .then((patientResult) => {
      return createRequest({
        patientId: patientResult.id,
        isHighPriority: formData.submitTo.highPriority,
        topicForDiscussion: formData.submitTo.reasonsForRequest,
        preferredPhysicianId: formData.submitTo.preferredPhysicianId,
        templateId: formData.templateId || '',
        organizationId: formData.organizationId || '',
        workflowId: formData.workflowId || ''
      });
    })
    .then((eventResult) => {
      onSuccess(eventResult);
    })
    .catch((error) => console.error(error));
}

export { NewRequestSubmission as default };
